import type { AutomationTimelineEntry, FlowDefinition } from '../types/api';

const STEP_LABELS: Record<string, string> = {
  wait: 'Wait',
  condition: 'Condition',
  create_coupon: 'Create discount code',
  send_template: 'Send email',
  update_contact: 'Update contact',
};

function unit(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Client-side display version of core `formatDuration`
 * (`packages/core/src/flows/timeline.ts`, now also English):
 * 172800 → "2 days", 93600 → "1 day and 2 hours".
 */
export function formatDuration(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(unit(days, 'day', 'days'));
  if (hours > 0) parts.push(unit(hours, 'hour', 'hours'));
  if (minutes > 0 && days === 0) parts.push(unit(minutes, 'minute', 'minutes'));
  return parts.length > 0 ? parts.join(' and ') : unit(seconds, 'second', 'seconds');
}

function describeCheck(step: FlowDefinition['steps'][number]): string {
  const label = String(step.label ?? '?');
  switch (step.check) {
    case 'no_order_since_enrollment':
      return 'Only continue if the contact has not bought since the flow started';
    case 'cart_not_recovered':
      return 'Only continue if the cart is still not purchased';
    case 'no_cart_since_enrollment':
      return 'Only continue if the contact has not added anything to the cart since the flow started';
    case 'label_present':
      return `Only continue if the contact has the tag ${label}`;
    case 'label_absent':
      return `Only continue if the contact does not have the tag ${label}`;
    case 'no_engagement_days':
      return `Only continue if the contact has not opened or clicked in ${String(step.days ?? 0)} days`;
    default:
      return `Condition: ${String(step.check ?? '?')}`;
  }
}

/**
 * Client-side display version of core `describeStep`
 * (`packages/core/src/flows/timeline.ts`, now also English).
 */
export function describeStep(step: FlowDefinition['steps'][number]): string {
  switch (step.type) {
    case 'wait':
      return typeof step.seconds === 'number'
        ? `Wait ${formatDuration(step.seconds)}`
        : 'Wait (unknown duration)';
    case 'condition':
      return `${describeCheck(step)} — otherwise ${step.onFail === 'stop' ? 'the flow stops' : 'the next step is skipped'}`;
    case 'create_coupon':
      return `Create discount code ${String(step.prefix ?? '')}xxxx (${String(step.percent ?? '?')} %, valid ${String(step.validDays ?? '?')} days)`;
    case 'send_template':
      return `Send email: "${String(step.subject ?? '')}"`;
    case 'update_contact':
      if (step.action === 'set_field') {
        return `Set the contact field ${String(step.key ?? '?')} to "${String(step.value ?? '')}"`;
      }
      if (step.action === 'add_label')
        return `Add the tag ${String(step.label ?? '?')} to the contact`;
      if (step.action === 'remove_label') {
        return `Remove the tag ${String(step.label ?? '?')} from the contact`;
      }
      return 'Stop sending to the contact (inactive)';
    default:
      return STEP_LABELS[step.type] ?? step.type;
  }
}

/**
 * Client-side English display version of `packages/core/src/flows/timeline.ts`'s
 * `previewTimeline()` (also used by the fetch mock) — used by the flow editor to update its docked timeline live from
 * unsaved form state, without a round trip to the API.
 */
export function previewTimeline(definition: FlowDefinition): AutomationTimelineEntry[] {
  let offsetSeconds = 0;
  const entries: AutomationTimelineEntry[] = [];
  for (const step of definition.steps) {
    const entry: AutomationTimelineEntry = {
      stepId: step.id,
      type: step.type,
      offsetSeconds,
      description: describeStep(step),
    };
    if (step.type === 'send_template' && typeof step.purpose === 'string') {
      entry.purpose = step.purpose;
    }
    if (step.type === 'condition') {
      if (typeof step.check === 'string') entry.check = step.check;
      if (typeof step.onFail === 'string') entry.onFail = step.onFail;
    }
    entries.push(entry);
    if (step.type === 'wait' && typeof step.seconds === 'number') {
      offsetSeconds += step.seconds;
    }
  }
  return entries;
}

/** "0" → "Right away", else the largest whole unit that fits (days, then hours, then minutes). */
export function formatOffset(seconds: number): string {
  if (seconds <= 0) return 'Right away';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (days > 0) return hours > 0 ? `${days}d ${hours}h later` : `${days}d later`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}min later` : `${hours}h later`;
  return `${Math.max(1, minutes)}min later`;
}

export const STEP_TYPE_LABELS = STEP_LABELS;
